import axios, { CancelTokenSource } from "axios";
import { ActionTree } from "vuex";
import { RefreshGridMode } from "@/models/leads/ContactForm";
import { DataSourceResult } from "@/models/DataSourceResult";
import { FilterCollection } from "@/models/FilterCollection";
import { GridColumn } from "@/models/GridColumn";
import { MetadataMaskyooStatus } from "@/models/MetadataMaskyooStatus";
import { ChannelMultiselectItem, MultiselectItem } from "@/models/MultiselectItem";
import { PhoneStatus, PhoneWithChannel } from "@/models/phones/PhoneWithChannel";
import { Result } from "@/models/Result";
import { TableSettings } from "@/models/TableSettings";
import { PhoneLeadStats } from "@/models/leads/LeadStatistic";
import { PhonesPageData } from "@/models/phones/PhonesPageData";
import { PhonesState } from "@/models/states/PhonesState";
import { RootState } from "@/models/states/RootState";
import ApiService, { CommonMultiselectPayload } from "@/services/api/ApiService";
import PhonesApiService, { PhonesPayload } from "@/services/api/PhonesApiService";

const actions: ActionTree<PhonesState, RootState> = {
  getChannels({ commit }, payload: CommonMultiselectPayload): Promise<ChannelMultiselectItem[]> {
    commit("setChannels", []);
    return new Promise((resolve, reject) => {
      ApiService.getChannels(payload)
        .then((data: ChannelMultiselectItem[]) => {
          commit("setChannels", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  
  getClients({ commit }): Promise<MultiselectItem[]> {
    return new Promise((resolve, reject) => {
      ApiService.getClients()
        .then((data: MultiselectItem[]) => {
          commit("setClients", data);
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  
  getPhones({ commit, state }, payload: PhonesPayload): Promise<DataSourceResult> {
    commit("setPhones", null);
    return new Promise((resolve, reject) => {
      let axiosSource: CancelTokenSource = axios.CancelToken.source();
      commit("setCancelTokenSource", axiosSource);
      PhonesApiService.getPhones(payload, axiosSource).then((data: DataSourceResult) => {
        if (data.Data && state.sources.length === 0) {
          const phones: PhoneWithChannel[] = data.Data;
          let sources: ChannelMultiselectItem[] = phones.filter((phone: PhoneWithChannel) => phone.ChannelID || phone.ProjectID)
                .map((i: PhoneWithChannel) => { return {
                  ID: i.ChannelID ? i.ChannelID.toString() : (i.ProjectID ? `${ i.ProjectID }:${ i.ProjectTypeID }` : ""),
                  ChannelMetadataType: i.ChannelMetadataTypeID,
                  ChannelTitle: i.ChannelName,
                  Title: i.ChannelName
                }}).filter((v, i, a) => v !== null && v.Title !== "" && a.indexOf(v) === i).sort();
          sources = [...new Map(sources.map(item => [item.ID, item])).values()];  // filter unique by ID
          commit("setSources", sources);
        }
        commit("setPhones", data);
        commit("setCancelTokenSource", null);
        resolve(data);
      }).catch((error) => {
        reject(error);
      });
    });
  },

  getPhonesPageData({ commit }): Promise<PhonesPageData> {
    commit("setPhonesPageData", null);
    return new Promise((resolve, reject) => {
      PhonesApiService.getPhonesPageData()
        .then((data: Result) => {
          commit("setPhonesPageData", data.Data);
          resolve(data.Data as PhonesPageData);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  setCancelTokenSource({ commit }, cancelTokenSource: CancelTokenSource) {
    commit("setCancelTokenSource", cancelTokenSource);
  },

  setColumns({ commit }, columns: GridColumn[]): void {
    commit("setColumns", columns);
  },

  setColumnsFilters({ commit }, columnsFilters: FilterCollection): void {
    commit("setColumnsFilters", columnsFilters);
  },

  setDatePeriod({ commit }, datePeriod: Date[]): void {
    commit("setDatePeriod", datePeriod);
  },

  setNeedToRefreshPhonesGrid({ commit }, needToRefreshPhonesGrid: RefreshGridMode): void {
    commit("setNeedToRefreshPhonesGrid", needToRefreshPhonesGrid);
  },

  setPage({ commit }, page: number): void {
    commit("setPage", page);
  },

  setPhoneLeadStats({ commit }, phoneLeadStats: PhoneLeadStats): void {
    commit("setPhoneLeadStats", phoneLeadStats);
  },

  setPhones({ commit }, phones: DataSourceResult): void {
    commit("setPhones", phones);
  },

  setSearchValue({ commit }, searchValue: string): void {
    commit("setSearchValue", searchValue);
  },

  setSelectedCallStatus({ commit }, selectedCallStatus?: MetadataMaskyooStatus): void {
    commit("setSelectedCallStatus", selectedCallStatus);
  },

  setSelectedChannelMetadataTypesIds({ commit }, selectedChannelMetadataTypesIds: number[]): void {
    commit("setSelectedChannelMetadataTypesIds", selectedChannelMetadataTypesIds);
  },

  setSelectedClientsIds({ commit }, selectedClientsIds: number[]): void {
    commit("setSelectedClientsIds", selectedClientsIds);
  },

  setSelectedPhone({ commit }, selectedPhone?: PhoneWithChannel): void {
    commit("setSelectedPhone", selectedPhone);
  },

  setSelectedPhonesGridColumnsIds({ commit }, selectedPhonesGridColumnsIds: number[]): void {
    commit("setSelectedPhonesGridColumnsIds", selectedPhonesGridColumnsIds);
  },

  setSelectedPhoneStatus({ commit }, selectedPhoneStatus?: PhoneStatus): void {
    commit("setSelectedPhoneStatus", selectedPhoneStatus);
  },

  setSelectedSourcesIds({ commit }, sources: string[]): void {
    commit("setSelectedSourcesIds", sources);
  },

  setShowGraphs({ commit }, showGraphs: boolean): void {
    commit("setShowGraphs", showGraphs);
  },

  setSortDir({ commit }, sortDir: string): void {
    commit("setSortDir", sortDir);
  },

  setSortField({ commit }, sortField: string): void {
    commit("setSortField", sortField);
  },

  setSources({ commit }, sources: ChannelMultiselectItem[]): void {
    commit("setSources", sources);
  },

  setTableSettings({ commit }, tableSettings: TableSettings): void {
    commit("setTableSettings", tableSettings);
  },
};
export default actions;
